import 'dayjs/locale/de'
import 'dayjs/locale/en'
import 'dayjs/locale/es'
import 'dayjs/locale/fr'
import 'dayjs/locale/it'
import 'dayjs/locale/pt'
// Simplified Chinese is 'zh' in i18next but 'zh-cn' in dayjs/moment.
// See shared/utils/getDateLibLocale for the mapping used at the call sites.
import 'dayjs/locale/zh-cn'
import i18next, { InitOptions } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import 'moment/locale/de'
import 'moment/locale/es'
import 'moment/locale/fr'
import 'moment/locale/it'
import 'moment/locale/pt'
// Registers zh-cn on the shared global moment instance for remote micro-frontends.
// NOTE: moment has no bare 'zh' locale - importing 'moment/locale/zh' fails the build.
import 'moment/locale/zh-cn'
import { initReactI18next } from 'react-i18next'

import deAuth from './de/auth.json'
import deCommon from './de/common.json'
import dePublic from './de/public.json'
import deReadableFileTypes from './de/readableFileTypes.json'
import deTranslation from './de/translation.json'
import enAuth from './en/auth.json'
import enCommon from './en/common.json'
import enPublic from './en/public.json'
import enReadableFileTypes from './en/readableFileTypes.json'
import enTranslation from './en/translation.json'
import esAuth from './es/auth.json'
import esCommon from './es/common.json'
import esPublic from './es/public.json'
import esReadableFileTypes from './es/readableFileTypes.json'
import esTranslation from './es/translation.json'
import frAuth from './fr/auth.json'
import frCommon from './fr/common.json'
import frPublic from './fr/public.json'
import frReadableFileTypes from './fr/readableFileTypes.json'
import frTranslation from './fr/translation.json'
import itAuth from './it/auth.json'
import itCommon from './it/common.json'
import itPublic from './it/public.json'
import itReadableFileTypes from './it/readableFileTypes.json'
import itTranslation from './it/translation.json'
import { claimLanguage, lockLanguageKey } from './lockLanguageKey'
import ptAuth from './pt/auth.json'
import ptCommon from './pt/common.json'
import ptPublic from './pt/public.json'
import ptReadableFileTypes from './pt/readableFileTypes.json'
import ptTranslation from './pt/translation.json'
import zhAuth from './zh/auth.json'
import zhCommon from './zh/common.json'
import zhPublic from './zh/public.json'
import zhReadableFileTypes from './zh/readableFileTypes.json'
import zhTranslation from './zh/translation.json'

export const resources = {
  en: {
    translation: enTranslation,
    readableFileTypes: enReadableFileTypes,
    auth: enAuth,
    common: enCommon,
    public: enPublic
  },
  de: {
    translation: deTranslation,
    readableFileTypes: deReadableFileTypes,
    auth: deAuth,
    common: deCommon,
    public: dePublic
  },
  fr: {
    translation: frTranslation,
    readableFileTypes: frReadableFileTypes,
    auth: frAuth,
    common: frCommon,
    public: frPublic
  },
  es: {
    translation: esTranslation,
    readableFileTypes: esReadableFileTypes,
    auth: esAuth,
    common: esCommon,
    public: esPublic
  },
  it: {
    translation: itTranslation,
    readableFileTypes: itReadableFileTypes,
    auth: itAuth,
    common: itCommon,
    public: itPublic
  },
  pt: {
    translation: ptTranslation,
    readableFileTypes: ptReadableFileTypes,
    auth: ptAuth,
    common: ptCommon,
    public: ptPublic
  },
  // Simplified Chinese. Deliberately keyed 'zh' rather than 'zh-Hans': the two-letter
  // form is what convertDetectedLanguage below can resolve (it strips everything after
  // the first '-'), and it is what ends up in localStorage, in the /:lang/public/*
  // route prefixes, and in the POST user/current/lang payload.
  zh: {
    translation: zhTranslation,
    readableFileTypes: zhReadableFileTypes,
    auth: zhAuth,
    common: zhCommon,
    public: zhPublic
  }
}

const convertDetectedLanguage = (lang: string) => {
  const validLanguages = Object.keys(resources)
  const langCountryRemoved = lang.split('-')[0]
  const validLanguage = validLanguages.includes(langCountryRemoved)

  return validLanguage ? langCountryRemoved : 'en'
}

const sharedOptions: InitOptions = {
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false
  },
  resources,
  debug: false,
  detection: {
    // The detector's default order puts `cookie` ahead of `localStorage`, so a
    // stale `i18next` cookie outranks the stored choice on every boot.
    order: ['querystring', 'localStorage', 'navigator'],
    caches: ['localStorage'],
    convertDetectedLanguage: convertDetectedLanguage
  }
}

// required for avoiding conflict between apps
export const i18nextContainer = i18next.createInstance()

lockLanguageKey()

// Registered before init(): the first languageChanged is emitted during init,
// so a listener attached afterwards never sees it.
i18nextContainer.on('languageChanged', lng => {
  window.document.documentElement.lang = lng
  claimLanguage(lng)
})

i18nextContainer.use(LanguageDetector).use(initReactI18next).init(sharedOptions)
